import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";
import type { Tool } from "@/tools";

/** Horizontal tool card used on the homepage listing (grouped by category). */
export function HomeToolCard({
  tool,
  action,
}: {
  tool: Tool;
  action?: ReactNode;
}) {
  return (
    <div className="group relative rounded-2xl overflow-hidden transition-all duration-500 bg-card border border-border card-elevated hover:translate-y-[-2px] hover:card-elevated-hover dark:bg-card/80 dark:backdrop-blur-xl shimmer-hover">
      <div className="absolute top-0 left-0 right-0 h-12 dark:hidden pointer-events-none card-top-glow opacity-50 group-hover:opacity-75 transition-opacity duration-300" />
      <div className="absolute top-0 left-0 right-0 h-[2px] hidden dark:block bg-gradient-to-r from-transparent via-primary/50 to-transparent group-hover:via-primary transition-all duration-300" />

      {action ? (
        <div className="absolute right-3 top-3 z-10">{action}</div>
      ) : null}

      <Link to={tool.basePath} className="block">
        <div className="relative p-5 md:p-7">
          <div className="flex items-center gap-4 md:gap-6">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl border flex items-center justify-center overflow-hidden transition-all duration-500 bg-primary/[0.06] border-primary/15 group-hover:bg-primary/10 group-hover:border-primary/25 dark:bg-gradient-to-br dark:from-primary/10 dark:via-primary/5 dark:to-accent/5 dark:border-primary/15 dark:group-hover:border-primary/30 dark:group-hover:shadow-lg dark:group-hover:shadow-primary/10">
                {tool.icon.startsWith("/") ? (
                  <img
                    src={tool.icon}
                    alt=""
                    className="w-12 h-12 md:w-14 md:h-14 object-contain"
                  />
                ) : (
                  <span className="text-2xl md:text-3xl">{tool.icon}</span>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-base md:text-lg font-bold tracking-tight group-hover:text-primary transition-colors">
                  {tool.name()}
                </h4>
              </div>
              <p className="text-xs md:text-sm leading-relaxed line-clamp-2 text-muted-foreground">
                {tool.description()}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tool.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 text-[10px] font-medium rounded-md bg-muted text-muted-foreground border border-border dark:bg-muted/40 dark:border-border/30"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex-shrink-0">
              <div className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 bg-primary/10 group-hover:bg-primary group-hover:text-primary-foreground dark:border dark:border-primary/15 dark:group-hover:border-primary">
                <ArrowUpRight className="h-4 w-4 text-primary group-hover:text-primary-foreground transition-colors" />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
